import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useContractServices } from '../../context/ContractServicesContext';
import {
  Clock,
  Lock,
  Calculator,
  AlertCircle
} from 'lucide-react';
import skyLoader from "../../assets/home-assets/skyLoader.gif";

const StakingAdminPanel = ({ adminService, refreshKey = 0, onTransactionSubmit = () => { } }) => {
  const { services, isReady } = useContractServices();

  const [loading, setLoading] = useState(false);
  const [stakingInfo, setStakingInfo] = useState({
    minLockDuration: null,
    maxLockDuration: null,
    multiplierFactor: null
  });

  // Form states
  const [minLockDuration, setMinLockDuration] = useState('');
  const [maxLockDuration, setMaxLockDuration] = useState('');
  const [multiplierFactor, setMultiplierFactor] = useState('');

  // Fetch staking contract info
  useEffect(() => {
    const fetchStakingInfo = async () => {
      if (!isReady) return;

      try {
        setLoading(true);

        // Get staking info from contracts
        const minLockDays = await services.projectStaking.contract.minLockDuration();
        const maxLockDays = await services.projectStaking.contract.maxLockDuration();
        const multiplier = await services.projectStaking.contract.multiplierFactor();

        // Convert seconds to days and BigNumber to numbers
        const minDays = Math.floor(minLockDays.toNumber() / (24 * 60 * 60));
        const maxDays = Math.floor(maxLockDays.toNumber() / (24 * 60 * 60));

        // Get multiplier - the contract uses 1e12 precision
        const SCORE_PRECISION = 1e12;
        const multiplierValue = multiplier.toNumber() / SCORE_PRECISION;

        setStakingInfo({
          minLockDuration: minDays,
          maxLockDuration: maxDays,
          multiplierFactor: multiplierValue
        });
      } catch (error) {
        console.error('Error fetching staking info:', error);
        toast.error('Failed to fetch staking configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchStakingInfo();
  }, [isReady, services, refreshKey]);

  // Set minimum lock duration
  const handleSetMinLockDuration = async (e) => {
    e.preventDefault();
    if (!minLockDuration) return;

    try {
      setLoading(true);
      await adminService.setMinLockDuration(minLockDuration);
      toast.success(`Minimum lock duration set to ${minLockDuration} days`);

      // Call transaction submit callback
      onTransactionSubmit();

      // Update displayed info
      setStakingInfo(prev => ({
        ...prev,
        minLockDuration: parseInt(minLockDuration)
      }));

      setMinLockDuration('');
    } catch (error) {
      console.error('Error setting minimum lock duration:', error);
      toast.error('Failed to update minimum lock duration');
    } finally {
      setLoading(false);
    }
  };

  // Set maximum lock duration
  const handleSetMaxLockDuration = async (e) => {
    e.preventDefault();
    if (!maxLockDuration) return;

    try {
      setLoading(true);
      await adminService.setMaxLockDuration(maxLockDuration);
      toast.success(`Maximum lock duration set to ${maxLockDuration} days`);

      // Call transaction submit callback
      onTransactionSubmit();

      // Update displayed info
      setStakingInfo(prev => ({
        ...prev,
        maxLockDuration: parseInt(maxLockDuration)
      }));

      setMaxLockDuration('');
    } catch (error) {
      console.error('Error setting maximum lock duration:', error);
      toast.error('Failed to update maximum lock duration');
    } finally {
      setLoading(false);
    }
  };

  // Set multiplier factor
  const handleSetMultiplierFactor = async (e) => {
    e.preventDefault();
    if (!multiplierFactor) return;

    try {
      setLoading(true);
      await adminService.setMultiplierFactor(multiplierFactor);
      toast.success(`Multiplier factor set to ${multiplierFactor}`);

      // Call transaction submit callback
      onTransactionSubmit();

      // Update displayed info
      setStakingInfo(prev => ({
        ...prev,
        multiplierFactor: parseFloat(multiplierFactor)
      }));

      setMultiplierFactor('');
    } catch (error) {
      console.error('Error setting multiplier factor:', error);
      toast.error('Failed to update multiplier factor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Staking Configuration</h2>

      {/* Current Staking Settings */}
      <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
          <Lock className="h-5 w-5 text-indigo-500 mr-2" />
          Current Staking Settings
        </h3>

        {loading ? (
          <div className="flex justify-center">
            <div className="flex justify-center items-center h-100">
              <img
                src={skyLoader}
                alt="Loading..."
                className="w-100 h-100 object-contain"
              />
            </div>          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Minimum Lock Duration:</p>
              <p className="font-medium">
                {stakingInfo.minLockDuration !== null ? `${stakingInfo.minLockDuration} days` : 'Loading...'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Maximum Lock Duration:</p>
              <p className="font-medium">
                {stakingInfo.maxLockDuration !== null ? `${stakingInfo.maxLockDuration} days` : 'Loading...'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Multiplier Factor:</p>
              <p className="font-medium">
                {stakingInfo.multiplierFactor !== null ? stakingInfo.multiplierFactor : 'Loading...'}
              </p>
            </div>
          </div>
        )}

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
            <div className="text-sm text-blue-600">
              <p className="font-medium">Staking Rewards Formula:</p>
              <p className="mt-1">Stake Score = Amount × (1 + Duration Factor × Multiplier Factor)</p>
              <p className="mt-1">Where Duration Factor scales from 0 to 1 based on how close the lock duration is to the maximum.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Lock Duration Configuration */}
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
            <Clock className="h-5 w-5 text-indigo-500 mr-2" />
            Lock Duration Configuration
          </h3>

          {/* Min Lock Duration */}
          <form onSubmit={handleSetMinLockDuration} className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Lock Duration (days)
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={minLockDuration}
                onChange={(e) => setMinLockDuration(e.target.value)}
                className="p-2 border border-gray-300 rounded-md w-full"
                placeholder="e.g., 7"
                min="1"
                max={stakingInfo.maxLockDuration ? stakingInfo.maxLockDuration - 1 : 89}
              />
              <button
                type="submit"
                disabled={loading || !minLockDuration}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Set
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Must be less than the maximum lock duration
            </p>
          </form>

          {/* Max Lock Duration */}
          <form onSubmit={handleSetMaxLockDuration}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Maximum Lock Duration (days)
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={maxLockDuration}
                onChange={(e) => setMaxLockDuration(e.target.value)}
                className="p-2 border border-gray-300 rounded-md w-full"
                placeholder="e.g., 730"
                min={stakingInfo.minLockDuration ? stakingInfo.minLockDuration + 1 : 2}
                max="3650"
              />
              <button
                type="submit"
                disabled={loading || !maxLockDuration}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Set
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Must be greater than the minimum lock duration and less than 3650 days (10 years)
            </p>
          </form>
        </div>

        {/* Reward Multiplier Configuration */}
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
            <Calculator className="h-5 w-5 text-indigo-500 mr-2" />
            Reward Multiplier Configuration
          </h3>

          {/* Multiplier Factor */}
          <form onSubmit={handleSetMultiplierFactor}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Multiplier Factor
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={multiplierFactor}
                onChange={(e) => setMultiplierFactor(e.target.value)}
                className="p-2 border border-gray-300 rounded-md w-full"
                placeholder="e.g., 2"
                min="1"
                max="19"
                step="0.1"
              />
              <button
                type="submit"
                disabled={loading || !multiplierFactor}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Set
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Must be between 1 and 19 (controlling maximum score multiplier from 2x to 20x)
            </p>
          </form>

          <div className="mt-6 p-3 bg-gray-50 border border-gray-200 rounded-md">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Multiplier Effect</h4>
            <p className="text-xs text-gray-600 mb-2">
              The multiplier factor determines how much staking rewards scale with lock duration:
            </p>
            <ul className="text-xs text-gray-600 space-y-1 list-disc pl-4">
              <li>Minimum lock duration: 1.0x</li>
              <li>Maximum lock duration: (1 + Multiplier Factor)x</li>
              <li>Example with factor 2: Stakes can get up to 3x multiplier</li>
              <li>Example with factor 5: Stakes can get up to 6x multiplier</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StakingAdminPanel; 